import { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  CreditCardIcon, 
  DollarSignIcon, 
  SearchIcon, 
  DownloadIcon,
  EyeIcon,
  RefreshCwIcon,
  AlertCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  AlertTriangleIcon
} from 'lucide-react';
import { billingAPI } from '../../lib/api';
import { useSettings } from '../../contexts/SettingsContext';
import { CreateBillingModal, PaymentModal, BillingDetailsModal } from '../../components/Billing';

interface BillItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

interface Patient {
  id: string;
  patientNumber: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
}

interface Doctor {
  user: {
    firstName: string;
    lastName: string;
  };
}

interface Appointment {
  id: string;
  doctor: Doctor;
}

interface Billing {
  id: string;
  amount: number;
  tax: number;
  discount: number;
  totalAmount: number;
  paymentStatus: 'PENDING' | 'PAID' | 'PARTIAL' | 'OVERDUE' | 'CANCELLED';
  paymentMethod?: string;
  transactionId?: string;
  dueDate: string;
  paidDate?: string;
  createdAt: string;
  patient: Patient;
  appointment?: Appointment;
  billItems: BillItem[];
}

interface BillingStats {
  revenue: {
    total: number;
    monthly: number;
    today: number;
  };
  pending: {
    amount: number;
    count: number;
  };
  overdue: {
    amount: number;
    count: number;
  };
  invoices: {
    total: number;
    paid: number;
    pending: number;
    overdue: number;
  };
  recentPayments: Array<{
    id: string;
    patientName: string;
    amount: number;
    paidDate: string;
    paymentMethod: string;
  }>;
}

const BillingPage = () => {
  const { currency, dateFormat, timeFormat, initialized } = useSettings();
  const [billings, setBillings] = useState<Billing[]>([]);
  const [stats, setStats] = useState<BillingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedBilling, setSelectedBilling] = useState<Billing | null>(null);

  const pageSize = 10;

  useEffect(() => {
    loadData();
  }, [currentPage, searchTerm, filterStatus, dateFormat, initialized]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: currentPage,
        limit: pageSize,
        search: searchTerm || undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        sortBy: 'createdAt',
        sortOrder: 'desc' as const
      };

      const [billingsResponse, statsResponse] = await Promise.all([
        billingAPI.getBillings(params),
        billingAPI.getBillingStats()
      ]);

      if (billingsResponse.success && statsResponse.success) {
        setBillings(billingsResponse.data.billings);
        setStats(statsResponse.data);
        setTotalPages(billingsResponse.data.pagination.totalPages);
        setTotalItems(billingsResponse.data.pagination.totalItems);
      } else {
        throw new Error('Failed to load billing data');
      }
    } catch (err) {
      console.error('Error loading billing data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleFilterChange = (status: string) => {
    setFilterStatus(status);
    setCurrentPage(1);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid': return 'badge-success';
      case 'partial': return 'badge-warning';
      case 'pending': return 'badge-secondary';
      case 'overdue': return 'badge-danger';
      case 'cancelled': return 'badge-secondary';
      default: return 'badge-secondary';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid': return <CheckCircleIcon className="h-4 w-4" />;
      case 'partial': return <ClockIcon className="h-4 w-4" />;
      case 'pending': return <ClockIcon className="h-4 w-4" />;
      case 'overdue': return <AlertTriangleIcon className="h-4 w-4" />;
      case 'cancelled': return <AlertCircleIcon className="h-4 w-4" />;
      default: return <ClockIcon className="h-4 w-4" />;
    }
  };

  const formatCurrency = (amount: number) => {
    // Use fallback currency while settings are loading
    const displayCurrency = initialized ? currency : 'USD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: displayCurrency
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const format = initialized ? dateFormat : 'MM/DD/YYYY';
    
    // Debug logging
    console.log('formatDate debug:', { initialized, dateFormat, format, dateString });
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    switch (format) {
      case 'DD/MM/YYYY':
        return `${day}/${month}/${year}`;
      case 'YYYY-MM-DD':
        return `${year}-${month}-${day}`;
      case 'MM/DD/YYYY':
      default:
        return `${month}/${day}/${year}`;
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const format = initialized ? timeFormat : '12';
    
    if (format === '24') {
      return date.toLocaleTimeString('en-US', { 
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } else {
      return date.toLocaleTimeString('en-US', { 
        hour12: true,
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit'
      });
    }
  };

  const handleViewBilling = (billingId: string) => {
    const billing = billings.find(b => b.id === billingId);
    if (billing) {
      setSelectedBilling(billing);
      setDetailsModalOpen(true);
    }
  };

  const handlePayBilling = (billingId: string) => {
    const billing = billings.find(b => b.id === billingId);
    if (billing) {
      setSelectedBilling(billing);
      setPaymentModalOpen(true);
    }
  };

  const handleCreateBilling = () => {
    setCreateModalOpen(true);
  };

  const handleModalSuccess = () => {
    loadData(); // Refresh data when any modal succeeds
  };

  if (loading && !refreshing) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Billing</h1>
            <p className="text-gray-600">Manage patient billing and payments</p>
          </div>
        </div>
        
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCwIcon className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-4" />
            <p className="text-gray-600">Loading billing data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Billing</h1>
            <p className="text-gray-600">Manage patient billing and payments</p>
          </div>
        </div>
        
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <AlertCircleIcon className="h-5 w-5 text-red-500 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Error Loading Billing Data</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
          <div className="mt-4">
            <button onClick={loadData} className="btn-primary">
              <RefreshCwIcon className="h-4 w-4 mr-2" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Billing</h1>
          <p className="text-gray-600">Manage patient billing and payments</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
          <button 
            onClick={handleRefresh}
            disabled={refreshing}
            className="btn-secondary w-full sm:w-auto"
          >
            <RefreshCwIcon className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button onClick={handleCreateBilling} className="btn-primary w-full sm:w-auto">
            <PlusIcon className="h-4 w-4 mr-2" />
            Create Invoice
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          <div className="bg-white rounded-lg shadow p-4 lg:p-6">
            <div className="flex items-start">
              <div className="bg-green-500 p-2 lg:p-3 rounded-lg flex-shrink-0">
                <DollarSignIcon className="h-5 w-5 lg:h-6 lg:w-6 text-white" />
              </div>
              <div className="ml-3 lg:ml-4 flex-1 min-w-0">
                <p className="text-xs lg:text-sm font-medium text-gray-600 mb-1">Total Revenue</p>
                <p className="text-lg lg:text-2xl font-semibold text-gray-900 break-words">
                  {formatCurrency(stats.revenue.total)}
                </p>
                <p className="text-xs text-gray-500 mt-1 break-words">
                  This month: {formatCurrency(stats.revenue.monthly)}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4 lg:p-6">
            <div className="flex items-start">
              <div className="bg-yellow-500 p-2 lg:p-3 rounded-lg flex-shrink-0">
                <ClockIcon className="h-5 w-5 lg:h-6 lg:w-6 text-white" />
              </div>
              <div className="ml-3 lg:ml-4 flex-1 min-w-0">
                <p className="text-xs lg:text-sm font-medium text-gray-600 mb-1">Pending</p>
                <p className="text-lg lg:text-2xl font-semibold text-gray-900 break-words">
                  {formatCurrency(stats.pending.amount)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {stats.pending.count} invoices
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 lg:p-6">
            <div className="flex items-start">
              <div className="bg-red-500 p-2 lg:p-3 rounded-lg flex-shrink-0">
                <AlertTriangleIcon className="h-5 w-5 lg:h-6 lg:w-6 text-white" />
              </div>
              <div className="ml-3 lg:ml-4 flex-1 min-w-0">
                <p className="text-xs lg:text-sm font-medium text-gray-600 mb-1">Overdue</p>
                <p className="text-lg lg:text-2xl font-semibold text-gray-900 break-words">
                  {formatCurrency(stats.overdue.amount)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {stats.overdue.count} invoices
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 lg:p-6">
            <div className="flex items-start">
              <div className="bg-blue-500 p-2 lg:p-3 rounded-lg flex-shrink-0">
                <CreditCardIcon className="h-5 w-5 lg:h-6 lg:w-6 text-white" />
              </div>
              <div className="ml-3 lg:ml-4 flex-1 min-w-0">
                <p className="text-xs lg:text-sm font-medium text-gray-600 mb-1">Total Invoices</p>
                <p className="text-lg lg:text-2xl font-semibold text-gray-900">{stats.invoices.total}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {stats.invoices.paid} paid, {stats.invoices.pending} pending
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-4 lg:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex-1 relative">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by patient name, invoice ID, or patient number..."
              className="input pl-10 w-full"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            <select
              className="input w-full sm:w-48"
              value={filterStatus}
              onChange={(e) => handleFilterChange(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="PAID">Paid</option>
              <option value="PARTIAL">Partial</option>
              <option value="PENDING">Pending</option>
              <option value="OVERDUE">Overdue</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
            <button className="btn-secondary w-full sm:w-auto">
              <DownloadIcon className="h-4 w-4 mr-2" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Billing Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell">Invoice</th>
                <th className="table-header-cell">Patient</th>
                <th className="table-header-cell">Date</th>
                <th className="table-header-cell">Services</th>
                <th className="table-header-cell">Total</th>
                <th className="table-header-cell">Due Date</th>
                <th className="table-header-cell">Status</th>
                <th className="table-header-cell">Actions</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {billings.length === 0 ? (
                <tr>
                  <td colSpan={8} className="table-cell text-center py-12">
                    <div className="text-gray-500">
                      <CreditCardIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                      <p className="text-lg font-medium">No billing records found</p>
                      <p className="text-sm">Get started by creating your first invoice.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                billings.map((billing) => (
                  <tr key={billing.id}>
                    <td className="table-cell">
                      <div className="text-sm font-medium text-gray-900">
                        {billing.id.substring(0, 8)}...
                      </div>
                      <div className="text-sm text-gray-500">
                        {billing.paymentMethod || 'Not specified'}
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="text-sm font-medium text-gray-900">
                        {billing.patient.user.firstName} {billing.patient.user.lastName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {billing.patient.patientNumber}
                      </div>
                      <div className="text-sm text-gray-500">
                        {billing.patient.user.email}
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="text-sm text-gray-900">
                        {formatDate(billing.createdAt)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatTime(billing.createdAt)}
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="space-y-1">
                        {billing.billItems.slice(0, 2).map((item) => (
                          <div key={item.id} className="text-sm text-gray-900">
                            {item.description}: {formatCurrency(Number(item.totalPrice))}
                          </div>
                        ))}
                        {billing.billItems.length > 2 && (
                          <div className="text-sm text-gray-500">
                            +{billing.billItems.length - 2} more items
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(Number(billing.totalAmount))}
                      </div>
                      {Number(billing.tax) > 0 && (
                        <div className="text-xs text-gray-500">
                          Tax: {formatCurrency(Number(billing.tax))}
                        </div>
                      )}
                      {Number(billing.discount) > 0 && (
                        <div className="text-xs text-green-600">
                          Discount: -{formatCurrency(Number(billing.discount))}
                        </div>
                      )}
                    </td>
                    <td className="table-cell">
                      <div className="text-sm text-gray-900">
                        {formatDate(billing.dueDate)}
                      </div>
                      {new Date(billing.dueDate) < new Date() && billing.paymentStatus !== 'PAID' && (
                        <div className="text-xs text-red-600 font-medium">
                          {Math.ceil((new Date().getTime() - new Date(billing.dueDate).getTime()) / (1000 * 60 * 60 * 24))} days overdue
                        </div>
                      )}
                    </td>
                    <td className="table-cell">
                      <span className={`badge ${getStatusColor(billing.paymentStatus)} flex items-center`}>
                        {getStatusIcon(billing.paymentStatus)}
                        <span className="ml-1 capitalize">
                          {billing.paymentStatus.toLowerCase()}
                        </span>
                      </span>
                      {billing.paidDate && (
                        <div className="text-xs text-gray-500 mt-1">
                          Paid: {formatDate(billing.paidDate)}
                        </div>
                      )}
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => handleViewBilling(billing.id)}
                          className="p-1 text-gray-400 hover:text-gray-500"
                          title="View Details"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button className="p-1 text-gray-400 hover:text-gray-500" title="Download">
                          <DownloadIcon className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => handleViewBilling(billing.id)}
                          className="btn-secondary text-xs px-2 py-1"
                        >
                          View
                        </button>
                        {billing.paymentStatus !== 'PAID' && billing.paymentStatus !== 'CANCELLED' && (
                          <button 
                            onClick={() => handlePayBilling(billing.id)}
                            className="btn-primary text-xs px-2 py-1"
                          >
                            Pay
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button 
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="btn-secondary disabled:opacity-50"
              >
                Previous
              </button>
              <button 
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="btn-secondary disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * pageSize, totalItems)}
                  </span> of{' '}
                  <span className="font-medium">{totalItems}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button 
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="btn-secondary rounded-l-md disabled:opacity-50"
                  >
                    Previous
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-2 text-sm border border-gray-300 ${
                          currentPage === page
                            ? 'bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  
                  <button 
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="btn-secondary rounded-r-md disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Recent Payments Section */}
      {stats && stats.recentPayments.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Payments</h3>
          <div className="space-y-3">
            {stats.recentPayments.slice(0, 5).map((payment) => (
              <div key={payment.id} className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">{payment.patientName}</p>
                  <p className="text-sm text-gray-500">
                    {payment.paymentMethod} • {new Date(payment.paidDate).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-sm font-medium text-green-600">
                  {formatCurrency(payment.amount)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modals */}
      <CreateBillingModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={handleModalSuccess}
      />

      <PaymentModal
        isOpen={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        onSuccess={handleModalSuccess}
        billing={selectedBilling}
      />

      <BillingDetailsModal
        isOpen={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        billing={selectedBilling}
        onPayment={() => {
          setDetailsModalOpen(false);
          setPaymentModalOpen(true);
        }}
      />
    </div>
  );
};

export default BillingPage; 